import * as d3 from 'd3';

class FromToNodePlotView extends HTMLElement {
    // Save dimensions between renders
    static dimensions = {
        width: 800,
        height: 600
    };

    connectedCallback() {
        
        // Listen for window resize to adjust size
        window.addEventListener('resize', this.handleResize.bind(this));
        
        // Listen for HTMX updates
        document.body.addEventListener('htmx:afterSwap', this.handleHtmxUpdate.bind(this));
        
        // Initial rendering
        this.renderFromToNodePlot();
    }
    
    disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize.bind(this));
        document.body.removeEventListener('htmx:afterSwap', this.handleHtmxUpdate.bind(this));
    }
    
    handleResize() {
        if (document.body.contains(this)) {
            const container = this.querySelector('#from-to-node-container');
            if (container) {
                const rect = container.getBoundingClientRect();
                if (rect.width > 10 && rect.height > 10) {
                    // Update our static dimensions when the size changes
                    FromToNodePlotView.dimensions = {
                        width: rect.width,
                        height: rect.height
                    };
                }
            }
            this.renderFromToNodePlot();
        }
    }
    
    handleHtmxUpdate() {
        if (document.body.contains(this)) {
            this.renderFromToNodePlot();
        }
    }
    
    renderFromToNodePlot() {
        const dataEl = this.querySelector('#from-to-node-data');
        if (!dataEl) return;
        
        const data = JSON.parse(dataEl.getAttribute('x-data'));
        this.createFromToNodePlot(data);
    }

    createFromToNodePlot(data) {
        const container = this.querySelector('#from-to-node-container');
        if (!container) return;
        
        // IMPORTANT CHANGE: Use container dimensions if available, otherwise the saved ones
        const rect = container.getBoundingClientRect();
        const width = (rect.width > 10) ? rect.width : FromToNodePlotView.dimensions.width;
        const height = (rect.height > 10) ? rect.height : FromToNodePlotView.dimensions.height;
        
        // If it's a "real" size, save it for future use
        if (width > 10 && height > 10) {
            FromToNodePlotView.dimensions = { width, height };
        }
        
        console.log('FromToNodePlot dimensions:', width, height);

        d3.select(container).selectAll("svg").remove();
        
        // 1. Extract nodes and links
        const nodeMap = {};
        const links = [];
        Object.values(data.children).forEach(d => {
                // Check that both from and to exist and are not the same
                if (!d.from || !d.to || d.from === d.to) return;
                if (!nodeMap[d.from]) nodeMap[d.from] = { id: d.from };
                if (!nodeMap[d.to]) nodeMap[d.to] = { id: d.to };
                if (!d.from || !d.to || d.from === d.to) {
                console.warn("Skipping invalid link:", d);
                return;
        }
        links.push({ source: d.from, target: d.to, id: d.id });
        });
        
        const nodes = Object.values(nodeMap);
        // Set initial position for all nodes to the center (or randomize within the container)
        nodes.forEach((n, i) => {
                n.x = width / 2 + 120 * Math.cos(2 * Math.PI * i / nodes.length);
                n.y = height / 2 + 120 * Math.sin(2 * Math.PI * i / nodes.length);
        });
                const color = d3.scaleOrdinal(d3.schemeCategory10)
                        .domain(nodes.map(d => d.id));
                // 2. Create force simulation
                function boundingBoxForce(width, height, radius = 20) {
                return () => {
                        nodes.forEach(n => {
                                n.x = Math.max(radius, Math.min(width - radius, n.x));
                                n.y = Math.max(radius, Math.min(height - radius, n.y));
                        });
                };
        }

        const simulation = d3.forceSimulation(nodes)
                .force("link", d3.forceLink(links).id(d => d.id).distance(140))
                .force("charge", d3.forceManyBody().strength(-200))
                .force("center", d3.forceCenter(width / 2, height / 2))
                .force("bounding", boundingBoxForce(width, height, 24));

        
        const svg = d3.select(container)
                .append("svg")
                .attr("width", "100%")
                .attr("height", "100%")
                .attr("viewBox", `0 0 ${width} ${height}`)
                .attr("preserveAspectRatio", "xMidYMid meet");

        // Draw links
     svg.append("defs").append("marker")
        .attr("id", "arrowhead")
        .attr("viewBox", "0 -5 10 10")
        .attr("refX", 18) // Adjust so the arrow is nicely placed at the node
        .attr("refY", 0)
        .attr("markerWidth", 8)
        .attr("markerHeight", 8)
        .attr("orient", "auto")
        .append("path")
        .attr("d", "M0,-5L10,0L0,5")
        .attr("fill", "#aaa");

        // Draw links with arrow
        const link = svg.append("g")
        .attr("stroke", "#aaa")
        .attr("fill", "none")
        .selectAll("path")
        .data(links)
        .join("path")
        .attr("stroke-width", 2)
        .attr("marker-end", "url(#arrowhead)");

        simulation.on("tick", () => {
        link.attr("d", d => {
                const dx = d.target.x - d.source.x;
                const dy = d.target.y - d.source.y;
                const dr = Math.sqrt(dx * dx + dy * dy);
                return `M${d.source.x},${d.source.y}A${dr},${dr} 0 0,1 ${d.target.x},${d.target.y}`;
        });
        node
                .attr("cx", d => d.x)
                .attr("cy", d => d.y);
        svg.selectAll("text")
                .attr("x", d => d.x)
                .attr("y", d => d.y - 18);
        });

        // Draw nodes with unique color
        const node = svg.append("g")
                .attr("stroke", "#fff")
                .attr("stroke-width", 1.5)
                .selectAll("circle")
                .data(nodes)
                .join("circle")
                .attr("r", 12)
                .attr("fill", d => color(d.id)) // <-- unique color per node
                .call(drag(simulation));

        // Add labels
        svg.append("g")
                .selectAll("text")
                .data(nodes)
                .join("text")
                .attr("font-size", 10)
                .attr("dy", 4)
                .attr("text-anchor", "middle")
                .text(d => d.id);


        //  Drag function
        function drag(simulation) {
                function dragstarted(event, d) {
                        if (!event.active) simulation.alphaTarget(0.3).restart();
                        d.fx = d.x;
                        d.fy = d.y;
                }
                function dragged(event, d) {
                        d.fx = event.x;
                        d.fy = event.y;
                }
                function dragended(event, d) {
                        if (!event.active) simulation.alphaTarget(0);
                        d.fx = null;
                        d.fy = null;
                }
                return d3.drag()
                        .on("start", dragstarted)
                        .on("drag", dragged)
                        .on("end", dragended);
        }

};
}

if (!customElements.get('from-to-node-view')) {
        customElements.define('from-to-node-view', FromToNodePlotView);
} 